import { EmptyStatePanel, ErrorState, LoadingState } from '@playforge/ui';
import { BlockStack, Box, Card, InlineGrid, Page, Text } from '@shopify/polaris';
import { ScrollArea } from '../components/ScrollArea';
import { apiClient } from '../lib/api-client';
import { useApi } from '../lib/use-api';
import { syncOrdersQuietly } from '../lib/orders-sync';
import { formatPercent, type AnalyticsEvent, type AnalyticsOverview } from '../lib/types';

const EVENT_LABELS: Record<string, string> = {
  visitor: 'Visitor',
  game_open: 'Game opened',
  game_start: 'Game started',
  game_complete: 'Game completed',
  reward_won: 'Reward won',
  coupon_created: 'Coupon created',
  coupon_used: 'Coupon used',
  purchase: 'Purchase',
};

function Metric({ label, value, accent }: { label: string; value: string; accent?: string }): JSX.Element {
  return (
    <Card>
      <BlockStack gap="100">
        <Text as="span" tone="subdued" variant="bodySm">
          {label}
        </Text>
        <Text as="span" variant="heading2xl" fontWeight="bold">
          <span style={accent ? { color: accent } : undefined}>{value}</span>
        </Text>
      </BlockStack>
    </Card>
  );
}

/** A horizontal funnel bar; width is relative to the funnel's first (largest) step. */
function FunnelRow({ label, value, max, color }: { label: string; value: number; max: number; color: string }): JSX.Element {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 56px', alignItems: 'center', gap: 12 }}>
      <Text as="span" variant="bodySm">
        {label}
      </Text>
      <div style={{ background: '#f1f1f4', borderRadius: 8, height: 28, overflow: 'hidden' }}>
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            background: color,
            borderRadius: 8,
            transition: 'width .5s ease',
          }}
        />
      </div>
      <Text as="span" variant="bodySm" alignment="end" fontWeight="semibold">
        {value.toLocaleString()}
      </Text>
    </div>
  );
}

export function AnalyticsPage(): JSX.Element {
  // Reconcile order redemptions first so coupon-used / conversion reflect orders.
  const overview = useApi(async () => {
    await syncOrdersQuietly();
    return apiClient.get<AnalyticsOverview>('/analytics');
  }, []);
  const events = useApi(() => apiClient.get<AnalyticsEvent[]>('/analytics/events'), []);

  return (
    <Page title="Analytics" subtitle="How your games are performing">
      {overview.loading ? <LoadingState label="Loading analytics" /> : null}
      {overview.error ? <ErrorState message={overview.error.message} onRetry={overview.refetch} /> : null}

      {overview.data ? (
        <BlockStack gap="500">
          <InlineGrid columns={{ xs: 2, sm: 3, md: 6 }} gap="300">
            <Metric label="Game starts" value={overview.data.gameStarts.toLocaleString()} />
            <Metric label="Completed" value={overview.data.gameCompletes.toLocaleString()} />
            <Metric label="Rewards won" value={overview.data.rewardsWon.toLocaleString()} accent="#16a34a" />
            <Metric label="Coupons created" value={overview.data.couponsCreated.toLocaleString()} />
            <Metric label="Coupons used" value={overview.data.couponsUsed.toLocaleString()} accent="#16a34a" />
            <Metric label="Completion rate" value={formatPercent(overview.data.completionRate)} accent="#6366f1" />
          </InlineGrid>

          {overview.data.gameStarts === 0 ? (
            <Card>
              <EmptyStatePanel heading="No plays yet">
                <p>Publish a campaign and play a game on your storefront — metrics appear here in real time.</p>
              </EmptyStatePanel>
            </Card>
          ) : (
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  Conversion funnel
                </Text>
                <BlockStack gap="300">
                  <FunnelRow label="Game starts" value={overview.data.gameStarts} max={overview.data.gameStarts} color="#6366f1" />
                  <FunnelRow label="Completed" value={overview.data.gameCompletes} max={overview.data.gameStarts} color="#818cf8" />
                  <FunnelRow label="Rewards won" value={overview.data.rewardsWon} max={overview.data.gameStarts} color="#a5b4fc" />
                  <FunnelRow label="Coupons used" value={overview.data.couponsUsed} max={overview.data.gameStarts} color="#16a34a" />
                </BlockStack>
                <Text as="p" tone="subdued" variant="bodySm">
                  Redemption rate (coupons used ÷ created): {formatPercent(overview.data.redemptionRate)}
                </Text>
              </BlockStack>
            </Card>
          )}

          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">
                Recent activity
              </Text>
              {events.loading ? <LoadingState label="Loading activity" /> : null}
              {events.data && events.data.length === 0 ? (
                <Text as="p" tone="subdued">
                  No activity recorded yet.
                </Text>
              ) : null}
              {events.data && events.data.length > 0 ? (
                <ScrollArea maxHeight={360}>
                  <BlockStack gap="300">
                    {events.data.map((e) => (
                      <Box key={e.id} borderBlockEndWidth="025" borderColor="border" paddingBlockEnd="200">
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                          <Text as="span">{EVENT_LABELS[e.eventType] ?? e.eventType}</Text>
                          <Text as="span" tone="subdued" variant="bodySm">
                            {new Date(e.createdAt).toLocaleString()}
                          </Text>
                        </div>
                      </Box>
                    ))}
                  </BlockStack>
                </ScrollArea>
              ) : null}
            </BlockStack>
          </Card>
        </BlockStack>
      ) : null}
    </Page>
  );
}
